import { Briefcase, Calendar, Loader2, CheckCircle2, XCircle, TrendingUp, TrendingDown, Info, PauseCircle } from 'lucide-react';
import { JobScheduleMetrics as JobScheduleMetricsType } from '@/types/reports';
import { MetricCard, MetricComparison } from './MetricCard';
import { ComparisonMetrics } from '@/hooks/useComparisonMetrics';
import { calculateChange } from '@/utils/periodComparison';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface JobScheduleMetricsProps {
  metrics: JobScheduleMetricsType;
  comparisonMetrics?: ComparisonMetrics;
  comparisonLabel?: string;
  comparisonLoading?: boolean;
}

export const JobScheduleMetrics = ({ 
  metrics, 
  comparisonMetrics, 
  comparisonLabel,
  comparisonLoading 
}: JobScheduleMetricsProps) => {
  const formatPercentage = (value: number) => `${value.toFixed(1)}%`;

  // Helper to build comparison prop
  const getComparison = (
    current: number,
    previousKey: keyof ComparisonMetrics,
    isPositiveGood = true
  ): MetricComparison | undefined => {
    if (!comparisonMetrics || !comparisonLabel) return undefined;
    const change = calculateChange(current, comparisonMetrics[previousKey]);
    return { ...change, label: comparisonLabel, isPositiveGood };
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <h3 className="text-lg font-semibold text-foreground">Job Metrics</h3>
        <span className="text-sm text-muted-foreground">(by Scheduled Date)</span>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <Info className="h-4 w-4 text-muted-foreground" />
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p>All metrics are based on when jobs are scheduled. A job counts toward this period based on its booking date, not when it was created or completed.</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-2">
        <MetricCard
          title="Total Jobs"
          value={metrics.totalJobs}
          subtitle="Scheduled"
          icon={Briefcase}
          valueColor="text-primary"
          iconBgColor="bg-primary/10"
          comparison={getComparison(metrics.totalJobs, 'totalJobs', true)}
          comparisonLoading={comparisonLoading}
        />
        
        <MetricCard
          title="Scheduled"
          value={metrics.scheduledJobs}
          subtitle="Awaiting"
          icon={Calendar}
          valueColor="text-info"
          iconBgColor="bg-info/10"
          iconColor="text-info"
          comparison={getComparison(metrics.scheduledJobs, 'scheduledJobs', true)}
          comparisonLoading={comparisonLoading}
        />
        
        <MetricCard
          title="In Progress"
          value={metrics.inProgressJobs}
          subtitle="Working"
          icon={Loader2}
          valueColor="text-warning"
          iconBgColor="bg-warning/10"
          iconColor="text-warning"
          comparison={getComparison(metrics.inProgressJobs, 'inProgressJobs', true)}
          comparisonLoading={comparisonLoading}
        />
        
        <MetricCard
          title="On Hold"
          value={metrics.onHoldJobs}
          subtitle="Paused"
          icon={PauseCircle}
          valueColor="text-warning"
          iconBgColor="bg-warning/10"
          iconColor="text-warning"
        />
        
        <MetricCard
          title="Completed"
          value={metrics.completedJobs}
          subtitle="Finished"
          icon={CheckCircle2}
          valueColor="text-success"
          iconBgColor="bg-success/10"
          iconColor="text-success"
          comparison={getComparison(metrics.completedJobs, 'completedJobs', true)}
          comparisonLoading={comparisonLoading}
        />
        
        <MetricCard
          title="Cancelled"
          value={metrics.cancelledJobs}
          subtitle="Not done"
          icon={XCircle}
          valueColor="text-destructive"
          iconBgColor="bg-destructive/10"
          iconColor="text-destructive"
          comparison={getComparison(metrics.cancelledJobs, 'cancelledJobs', false)}
          comparisonLoading={comparisonLoading}
        />
        
        <MetricCard
          title="Completion Rate"
          value={formatPercentage(metrics.completionRate)}
          subtitle={`${metrics.completedJobs}/${metrics.totalJobs}`}
          icon={TrendingUp}
          valueColor="text-success"
          iconBgColor="bg-success/10"
          iconColor="text-success"
        />
        
        <MetricCard
          title="Cancellation Rate"
          value={formatPercentage(metrics.cancellationRate)}
          subtitle={`${metrics.cancelledJobs}/${metrics.totalJobs}`}
          icon={TrendingDown}
          valueColor="text-destructive"
          iconBgColor="bg-destructive/10"
          iconColor="text-destructive"
        />
      </div>
    </div>
  );
};
